import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  BrainCircuit, ArrowRight, ArrowLeft, Check, Sparkles, ShieldCheck, Clock,
  Loader2, Star, Gauge, Zap, Layers, Lightbulb, Target, Timer, MousePointerClick, Mail,
  Compass, Lock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useToast } from "../hooks/use-toast";
import { useMobileNav } from "../components/MobileBottomNav";
import Seo from "../components/Seo";
import { DISCOVER_QUESTIONS, ENCOURAGEMENT } from "../discover/questions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/discover`;
const LS_KEY = "mmk_discover_v1";
const GRADES = [5, 6, 7, 8];
const CODES = ["+91", "+1", "+44", "+971", "+65", "+61", "+880", "+977"];

const ASSESSMENT_URL = "/assessments";

const FACTORS = [
  { icon: Target, name: "Focus & Concentration", line: "How long attention holds and how fast it recovers." },
  { icon: BrainCircuit, name: "Dynamic IQ", line: "Reasoning measured as it performs, not one fixed number." },
  { icon: Zap, name: "Processing Speed", line: "How quickly information is taken in and acted on." },
  { icon: Layers, name: "Working Memory", line: "How much your child can hold and work with at once." },
  { icon: Lightbulb, name: "Natural Ability", line: "Innate aptitudes schooling has not yet surfaced." },
  { icon: Gauge, name: "Cognitive Capacity", line: "The overall headroom available for learning." },
];

function useNoIndex() {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => { document.head.removeChild(m); };
  }, []);
}

const loadLS = () => { try { return JSON.parse(localStorage.getItem(LS_KEY) || "null"); } catch { return null; } };
const saveLS = (o) => localStorage.setItem(LS_KEY, JSON.stringify(o));
const clearLS = () => localStorage.removeItem(LS_KEY);

export default function Discover() {
  useNoIndex();
  const { toast } = useToast();
  const [phase, setPhase] = useState("landing"); // landing | quiz | calc | gate | report
  const { setHidden } = useMobileNav();
  useEffect(() => { setHidden(phase !== "landing"); }, [phase, setHidden]);
  useEffect(() => () => setHidden(false), [setHidden]);
  const [grade, setGrade] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const [report, setReport] = useState(null);
  const [resumeAvail, setResumeAvail] = useState(false);
  const [prior, setPrior] = useState(null); // {child, sessionId} of a completed run
  const qStart = useRef(Date.now());
  const activityStart = useRef(Date.now());

  // Restore on mount
  useEffect(() => {
    const s = loadLS();
    if (!s) return;
    const fresh = Date.now() - (s.savedAt || 0) < 24 * 3600 * 1000;
    if (s.submitted && s.sessionId) {
      setPrior({ child: s.child || "", sessionId: s.sessionId });
    } else if (fresh && s.sessionId && s.answers && Object.keys(s.answers).length > 0) {
      setResumeAvail(true);
    }
  }, []);

  const persist = (patch) => {
    const cur = loadLS() || {};
    saveLS({ ...cur, ...patch, savedAt: Date.now() });
  };

  const startQuiz = async (g) => {
    try {
      const r = await axios.post(`${API}/start`, {
        grade: g,
        referrer: document.referrer || "",
        device: navigator.userAgent,
        utm: Object.fromEntries(new URLSearchParams(window.location.search)),
      });
      setGrade(g); setSessionId(r.data.session_id); setAnswers({}); setIdx(0);
      persist({ sessionId: r.data.session_id, grade: g, answers: {}, idx: 0, submitted: false });
      setPrior(null); setResumeAvail(false);
      qStart.current = Date.now();
      activityStart.current = Date.now();
      setPhase("quiz");
    } catch (e) {
      const msg = e?.response?.status === 429 ? "Too many attempts. Please try again later." : "Could not start. Please try again.";
      toast({ title: "Please wait", description: msg });
    }
  };

  const resume = () => {
    const s = loadLS();
    setGrade(s.grade); setSessionId(s.sessionId); setAnswers(s.answers || {});
    const next = Math.min(Object.keys(s.answers || {}).length, 39);
    setIdx(next); setResumeAvail(false); qStart.current = Date.now(); activityStart.current = Date.now(); setPhase("quiz");
  };

  const q = DISCOVER_QUESTIONS[idx];
  const answered = Object.keys(answers).length;

  const choose = async (letter) => {
    const qno = q.no;
    const na = { ...answers, [qno]: letter };
    setAnswers(na);
    persist({ answers: na, idx });
    axios.post(`${API}/answer`, { session_id: sessionId, question_no: qno, answer: letter, time_ms: Date.now() - qStart.current }).catch(() => {});
    setTimeout(() => {
      if (idx < 39) { setDir(1); setIdx(idx + 1); qStart.current = Date.now(); }
      else { setPhase("calc"); setTimeout(() => setPhase("gate"), 1600); }
    }, 250);
  };

  const goBack = () => { if (idx > 0) { setDir(-1); setIdx(idx - 1); qStart.current = Date.now(); } };

  const onSubmitted = (rep) => {
    setReport(rep); persist({ submitted: true, child: rep.child_name || "" }); setPhase("report");
    window.scrollTo(0, 0);
  };

  const viewPrior = async () => {
    try {
      const r = await axios.get(`${API}/report/${prior.sessionId}`);
      setReport(r.data); setPhase("report"); window.scrollTo(0, 0);
    } catch { toast({ title: "Could not load your previous report" }); }
  };

  return (
    <div className="min-h-screen bg-[#faf7ff] flex flex-col">
      <Seo
        title="Discover How Your Child Naturally Learns — Free | MapMyKid"
        description="Take MapMyKid Discover: a free 10–15 minute activity for Grades 5 to 8. No signup. Your child answers 40 fun situations and you instantly get a visual report of their natural learning strengths."
        image="https://mapmykid.com/discover-og.jpg"
      />
      <Navbar />
      <div className="flex-1">
        {phase === "landing" && <Landing onStart={startQuiz} resumeAvail={resumeAvail} onResume={resume} prior={prior} onViewPrior={viewPrior} />}
        {phase === "quiz" && (
          <Quiz q={q} idx={idx} answered={answered} selected={answers[q.no]} dir={dir}
            onChoose={choose} onBack={goBack} startTs={activityStart.current} />
        )}
        {phase === "calc" && <Calculating />}
        {phase === "gate" && <Gate sessionId={sessionId} onDone={onSubmitted} toast={toast} />}
        {phase === "report" && report && <Report report={report} />}
      </div>
      <Footer />
    </div>
  );
}

/* ---------------- Landing ---------------- */
function Landing({ onStart, resumeAvail, onResume, prior, onViewPrior }) {
  const [g, setG] = useState(null);
  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 pt-24 sm:pt-28 pb-16">
      <div className="text-center animate-fade-up">
        <span className="inline-flex items-center gap-2 rounded-full bg-violet-100 px-4 py-1.5 text-[13px] font-bold text-violet-700"><Sparkles className="h-4 w-4" /> MapMyKid Discover</span>
        <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-slate-900">
          Discover how your child <span className="brand-gradient-text">naturally learns.</span>
        </h1>
        <p className="mt-4 text-lg text-slate-600">A free 10-15 minute activity for students in Grades 5 to 8. No signup. Instant visual report.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2.5 text-[13px] font-semibold text-slate-600">
          {["No login needed", "40 fun situations", "10-15 minutes", "Free report"].map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-violet-100 px-3.5 py-1.5"><Check className="h-3.5 w-3.5 text-teal-500" /> {t}</span>
          ))}
        </div>
      </div>

      {prior && (
        <div className="mt-8 rounded-2xl border border-violet-200 bg-violet-50 p-4 sm:p-5">
          <p className="text-[15px] font-semibold text-violet-900">You have already taken Discover Free{prior.child ? ` for ${prior.child}` : ""}.</p>
          <p className="mt-1 text-[14px] text-violet-800">Would you like to take it again for another child? You can take it as many times as you like, one child at a time.</p>
          <div className="mt-3 flex flex-wrap gap-2.5">
            <a href="#grade"><Button className="cta-gradient text-white rounded-full h-11 px-5 font-semibold" data-testid="discover-takeagain-btn">Take again for another child</Button></a>
            <Button onClick={onViewPrior} variant="outline" className="rounded-full h-11 px-5 font-semibold border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="discover-viewprior-btn">View my last report</Button>
          </div>
        </div>
      )}

      {resumeAvail && !prior && (
        <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-center justify-between gap-3">
          <p className="text-[15px] font-medium text-amber-900">You have an activity in progress.</p>
          <Button onClick={onResume} className="cta-gradient text-white rounded-full h-11 px-5 font-semibold" data-testid="discover-resume-btn">Continue</Button>
        </div>
      )}

      <div id="grade" className="mt-8 scroll-mt-24 rounded-3xl border border-violet-100 bg-white p-6 sm:p-8 shadow-xl shadow-violet-100/50">
        <Label className="text-[16px] font-semibold text-slate-800">Which grade is your child in?</Label>
        <div className="mt-4 grid grid-cols-4 gap-2.5">
          {GRADES.map((n) => (
            <button key={n} onClick={() => setG(n)} data-testid={`discover-grade-${n}`}
              className={`rounded-2xl border-2 py-4 text-[17px] font-extrabold transition-all ${g === n ? "cta-gradient text-white border-transparent shadow-lg" : "bg-white text-slate-700 border-violet-100 hover:border-violet-300"}`}>
              {n}
            </button>
          ))}
        </div>

        <div className="mt-6 rounded-2xl bg-violet-50 border border-violet-100 p-4">
          <p className="text-[14px] leading-relaxed text-violet-900">
            <strong>Please hand the device to your child now.</strong> There are no right or wrong answers; ask them to choose whatever feels most natural to them. Your report will be ready at the end.
          </p>
          <p className="mt-2.5 flex items-center gap-1.5 text-[13px] font-semibold text-violet-800"><Clock className="h-4 w-4" /> Tip: the activity has a recommended 15-minute timer, and your child can take a little longer if needed.</p>
        </div>

        <Button disabled={!g} onClick={() => onStart(g)} data-testid="discover-start-btn"
          className="mt-6 w-full cta-gradient text-white rounded-full h-14 text-[17px] font-bold disabled:opacity-50">
          Start the Activity <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
        <p className="mt-3 text-center text-[13px] text-slate-400">The child answers; the parent reads the report.</p>
      </div>
    </div>
  );
}

/* ---------------- Quiz ---------------- */
function Quiz({ q, idx, answered, selected, dir, onChoose, onBack, startTs }) {
  const pct = Math.round((answered / 40) * 100);
  const enc = ENCOURAGEMENT[idx]; // show when landing on question index 10/20/30 (i.e., after 10/20/30 answered)
  const [remain, setRemain] = useState(15 * 60);
  const [showTimeUp, setShowTimeUp] = useState(false);
  const [timeUpDismissed, setTimeUpDismissed] = useState(false);
  useEffect(() => {
    const tick = () => setRemain(Math.max(0, 15 * 60 - Math.floor((Date.now() - startTs) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startTs]);
  const timeUp = remain === 0;
  useEffect(() => {
    if (timeUp && !timeUpDismissed) setShowTimeUp(true);
  }, [timeUp, timeUpDismissed]);
  const mm = String(Math.floor(remain / 60)).padStart(2, "0");
  const ss = String(remain % 60).padStart(2, "0");
  const low = !timeUp && remain <= 120;
  return (
    <div className="pt-16 sm:pt-[72px]">
      <div className="sticky top-16 sm:top-[72px] z-20 bg-[#faf7ff]/95 backdrop-blur border-b border-violet-100">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 py-2.5">
          <div className="flex items-center justify-between">
            <span className="text-[13px] font-bold text-violet-600">MapMyKid Discover</span>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[13px] font-bold ${timeUp ? "bg-teal-50 text-teal-700" : low ? "bg-red-50 text-red-600" : "bg-violet-50 text-violet-700"}`} data-testid="discover-timer">
              <Timer className="h-4 w-4" /> {timeUp ? "Take your time" : `${mm}:${ss}`}
            </span>
          </div>
          <div className="mt-2 flex items-center justify-between text-[13px] font-semibold text-slate-500">
            <span data-testid="discover-progress-text">Question {idx + 1} of 40</span>
            <span>{40 - answered} left</span>
          </div>
          <div className="mt-1.5 h-2.5 w-full rounded-full bg-violet-100 overflow-hidden">
            <div className="h-full cta-gradient transition-all duration-300" style={{ width: `${Math.max(pct, (idx / 40) * 100)}%` }} />
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-2xl px-4 sm:px-6 pt-6 pb-24">
        <AnimatePresence>
          {showTimeUp && (
            <motion.div
              initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
              className="mb-4 rounded-2xl border border-teal-200 bg-teal-50 p-4 flex items-start gap-3"
              data-testid="discover-timeup-banner">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-teal-100 text-teal-600"><Clock className="h-5 w-5" /></span>
              <div className="flex-1">
                <p className="text-[15px] font-extrabold text-teal-900">The recommended 15 minutes are up — no worries!</p>
                <p className="mt-0.5 text-[14px] text-teal-800">You can keep going and finish at your own pace. Fifteen minutes is only a suggestion, not a hard stop.</p>
                <button onClick={() => { setShowTimeUp(false); setTimeUpDismissed(true); }} className="mt-2.5 rounded-full bg-teal-600 px-4 py-1.5 text-[13px] font-bold text-white" data-testid="discover-timeup-continue">
                  Got it, keep going
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        {idx > 0 && (
          <button onClick={onBack} className="mb-4 inline-flex items-center gap-1.5 text-[14px] font-semibold text-violet-700" data-testid="discover-back-btn">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
        )}
        {enc && (
          <div className="mb-4 rounded-full bg-teal-50 border border-teal-200 px-4 py-2 text-center text-[14px] font-bold text-teal-700">{enc}</div>
        )}
        <AnimatePresence mode="wait" initial={false}>
          <motion.div key={q.no}
            initial={{ opacity: 0, x: dir * 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -dir * 40 }}
            transition={{ duration: 0.22 }}>
            <p className="text-[13px] font-bold uppercase tracking-wide text-violet-500">Situation {q.no}</p>
            <h2 className="mt-1 text-2xl sm:text-3xl font-extrabold text-slate-900">{q.title}</h2>
            <p className="mt-3 text-[17px] leading-relaxed text-slate-700">{q.prompt}</p>
            <div className="mt-6 space-y-3">
              {["A", "B", "C", "D"].map((L) => (
                <button key={L} onClick={() => onChoose(L)} data-testid={`discover-option-${L}`}
                  className={`group w-full text-left rounded-2xl border-2 p-4 sm:p-5 transition-all flex items-start gap-3.5 ${selected === L ? "cta-gradient text-white border-transparent shadow-lg" : "bg-white border-violet-100 hover:border-violet-300 hover:shadow-md"}`}>
                  <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[14px] font-extrabold ${selected === L ? "bg-white/25 text-white" : "bg-violet-50 text-violet-700"}`}>{L}</span>
                  <span className="text-[16px] leading-snug font-medium pt-0.5">{q.options[L]}</span>
                </button>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ---------------- Calculating ---------------- */
function Calculating() {
  return (
    <div className="min-h-screen grid place-items-center px-4">
      <div className="text-center animate-fade-up">
        <div className="mx-auto grid h-20 w-20 place-items-center rounded-3xl cta-gradient text-white shadow-xl shadow-violet-300/50">
          <Loader2 className="h-10 w-10 animate-spin" />
        </div>
        <h2 className="mt-6 text-2xl font-extrabold text-slate-900">All done!</h2>
        <p className="mt-2 text-[16px] text-slate-600">Preparing your child's learning strengths report...</p>
      </div>
    </div>
  );
}

/* ---------------- Gate ---------------- */
function Gate({ sessionId, onDone, toast }) {
  const [f, setF] = useState({ parent_name: "", email: "", code: "+91", phone: "", city: "", child_name: "", company: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const up = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async () => {
    if (f.parent_name.trim().length < 2) return toast({ title: "Please add your full name" });
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) return toast({ title: "Please enter a valid email" });
    const digits = f.phone.replace(/\D/g, "");
    if (f.code === "+91" ? digits.length !== 10 : (digits.length < 6 || digits.length > 15))
      return toast({ title: "Please enter a valid phone number", description: f.code === "+91" ? "Indian numbers must be 10 digits." : "" });
    if (!f.city.trim()) return toast({ title: "Please add your city" });
    if (!consent) return toast({ title: "Please accept consent to see the report" });
    setLoading(true);
    try {
      const r = await axios.post(`${API}/submit`, {
        session_id: sessionId, parent_name: f.parent_name.trim(), email: f.email.trim(),
        phone: `${f.code} ${digits}`, city: f.city.trim(), child_name: f.child_name.trim(),
        consent: true, company: f.company,
      });
      onDone(r.data);
    } catch (e) {
      toast({ title: "Could not submit", description: e?.response?.data?.detail || "Please try again." });
    } finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-xl px-4 sm:px-6 pt-24 sm:pt-28 pb-16">
      <div className="text-center animate-fade-up">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900">Your report is ready. <span className="brand-gradient-text">Where should we send it?</span></h1>
        <p className="mt-3 text-[16px] text-slate-600">We'll show it on screen right away and email you a copy for your records.</p>
      </div>
      <div className="mt-8 rounded-3xl border border-violet-100 bg-white p-6 sm:p-8 shadow-xl shadow-violet-100/50 space-y-4">
        <div className="space-y-2"><Label className="text-[15px]">Parent's Full Name *</Label>
          <Input value={f.parent_name} onChange={up("parent_name")} className="h-12 text-[16px] rounded-xl" data-testid="discover-parent-name" /></div>
        <div className="space-y-2"><Label className="text-[15px]">Email ID *</Label>
          <Input type="email" value={f.email} onChange={up("email")} className="h-12 text-[16px] rounded-xl" data-testid="discover-email" /></div>
        <div className="space-y-2"><Label className="text-[15px]">WhatsApp / Mobile Number *</Label>
          <div className="flex gap-2">
            <select value={f.code} onChange={up("code")} className="h-12 rounded-xl border border-input bg-white px-2 text-[16px]" data-testid="discover-code">
              {CODES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <Input value={f.phone} onChange={up("phone")} inputMode="numeric" placeholder="10-digit number" className="h-12 text-[16px] rounded-xl flex-1" data-testid="discover-phone" />
          </div></div>
        <div className="space-y-2"><Label className="text-[15px]">City *</Label>
          <Input value={f.city} onChange={up("city")} className="h-12 text-[16px] rounded-xl" data-testid="discover-city" /></div>
        <div className="space-y-2"><Label className="text-[15px]">Child's First Name <span className="text-slate-400 font-normal">(optional)</span></Label>
          <Input value={f.child_name} onChange={up("child_name")} placeholder="To personalise the report" className="h-12 text-[16px] rounded-xl" data-testid="discover-child-name" /></div>
        {/* honeypot */}
        <input type="text" value={f.company} onChange={up("company")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
        <label className="flex items-start gap-3 cursor-pointer pt-1">
          <Checkbox checked={consent} onCheckedChange={setConsent} className="mt-1" data-testid="discover-consent" />
          <span className="text-[14px] leading-relaxed text-slate-600">I agree to receive my child's report and related information from MapMyKid on email and WhatsApp.</span>
        </label>
        <Button onClick={submit} disabled={loading} className="w-full cta-gradient text-white rounded-full h-14 text-[17px] font-bold" data-testid="discover-submit-btn">
          {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Preparing...</> : <>Show My Child's Report <ArrowRight className="ml-2 h-5 w-5" /></>}
        </Button>
        <p className="text-center text-[12px] text-slate-400">This activity is intended to be completed by a child with parental awareness. Only the parent's contact details are collected.</p>
      </div>
    </div>
  );
}

/* ---------------- Report ---------------- */
function Chart({ report }) {
  const gl = report.band_gridlines;
  const bounds = [0, ...gl, 1];
  return (
    <div>
      <div className="space-y-3.5">
        {report.profile.map((p) => (
          <ProfileRow key={p.code} p={p} gl={gl} />
        ))}
      </div>
      {/* x-axis interval labels */}
      <div className="relative mt-3 ml-0 sm:ml-[210px] h-5">
        {report.scale_intervals.map((s, i) => {
          const mid = (bounds[i] + bounds[i + 1]) / 2;
          return <span key={s + i} className="absolute -translate-x-1/2 text-[10px] sm:text-[11px] font-semibold text-slate-400 whitespace-nowrap" style={{ left: `${mid * 100}%` }}>{s}</span>;
        })}
      </div>
    </div>
  );
}

function ProfileRow({ p, gl }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setOpen((v) => !v)} className="w-full text-left flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4" data-testid={`discover-bar-${p.code}`}>
        <div className="sm:w-[200px] shrink-0">
          <div className="text-[15px] font-bold leading-tight" style={{ color: p.color }}>{p.parent_name}</div>
          <div className="text-[12px] text-slate-400 leading-tight">({p.technical_name})</div>
        </div>
        <div className="relative flex-1 min-w-0">
          {/* track */}
          <div className="relative h-8 w-full rounded-full bg-slate-100 overflow-hidden">
            {gl.map((frac, i) => (
              <span key={i} className="absolute top-0 bottom-0 w-px bg-white/70" style={{ left: `${frac * 100}%` }} />
            ))}
            <div className="absolute top-0 bottom-0 left-0 rounded-full transition-all duration-500"
              style={{ width: `${Math.max(p.bar_fraction * 100, 6)}%`, backgroundColor: p.color, opacity: p.is_core ? 1 : 0.42, boxShadow: p.is_core ? `0 0 0 2px rgba(255,255,255,0.9), 0 4px 14px ${p.color}66` : "none" }} />
          </div>
          {/* band chip */}
          <div className="mt-1 flex items-center gap-2">
            <span className="text-[12px] font-bold" style={{ color: p.is_core ? p.color : "#64748b" }}>{p.band}</span>
            {p.is_core && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-700">Core</span>}
          </div>
        </div>
      </button>
      {open && (
        <div className="mt-2 sm:ml-[216px] rounded-xl bg-white border border-violet-100 p-3.5">
          <p className="text-[14px] text-slate-600 leading-relaxed">{p.meaning}</p>
          <p className="mt-2.5 text-[13px] font-bold text-slate-700">Great for careers like:</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {p.careers.map((c) => (
              <span key={c} className="rounded-full px-2.5 py-1 text-[12px] font-semibold" style={{ backgroundColor: `${p.color}18`, color: p.color }}>{c}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Report({ report }) {
  const [showSticky, setShowSticky] = useState(false);
  const chartRef = useRef(null);
  const child = report.child_name || "Your child";
  useEffect(() => {
    const onScroll = () => {
      if (!chartRef.current) return;
      setShowSticky(chartRef.current.getBoundingClientRect().bottom < window.innerHeight);
    };
    window.addEventListener("scroll", onScroll); onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="pb-28">
      {/* header */}
      <div className="hero-gradient pt-24 sm:pt-28 pb-10">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/70 px-4 py-1.5 text-[13px] font-bold text-violet-700"><Star className="h-4 w-4 fill-violet-500 text-violet-500" /> MapMyKid Discover</span>
          <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold text-slate-900" data-testid="discover-report-title">{child}'s Learning Strengths Profile</h1>
          <p className="mt-2 text-[15px] text-slate-500">Grade {report.grade} &middot; {report.date}{report.duration_label ? ` · Time taken ${report.duration_label}` : ""}</p>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        {report.fast && (
          <div className="mt-6 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-center" data-testid="discover-fast-banner">
            <p className="text-[15px] font-extrabold text-amber-900">🤔 Hmmm! You finished in under 4 minutes.</p>
            <p className="mt-1 text-[14px] text-amber-800">Were you serious? 😅 Please make sure your child read all the questions carefully. For a truer picture, you can take the activity again.</p>
          </div>
        )}
        <div className="mt-6 rounded-2xl border border-teal-200 bg-teal-50 p-4 flex items-start gap-3" data-testid="discover-emailed-note">
          <Mail className="h-5 w-5 shrink-0 text-teal-600 mt-0.5" />
          <p className="text-[14px] text-teal-900 leading-relaxed">A copy of this report has been emailed{report.parent_email ? <> to <strong>{report.parent_email}</strong></> : ""}. Please check your <strong>Inbox</strong>, your <strong>Updates / Promotions</strong> tab, or even your <strong>Spam</strong> folder.</p>
        </div>

        {/* Core two */}
        <p className="mt-8 text-center text-[16px] leading-relaxed text-slate-600 max-w-2xl mx-auto">{report.framing}</p>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          {report.core.map((c) => (
            <div key={c.code} className="rounded-3xl bg-white border-2 p-6 shadow-xl shadow-violet-100/40" style={{ borderColor: c.color }}>
              <div className="flex items-center justify-between gap-2">
                <span className="grid h-12 w-12 place-items-center rounded-2xl text-white" style={{ backgroundColor: c.color }}><Sparkles className="h-6 w-6" /></span>
                <span className="rounded-full px-3 py-1 text-[13px] font-bold text-white" style={{ backgroundColor: c.color }}>{c.band}</span>
              </div>
              <h3 className="mt-4 text-[22px] font-extrabold leading-tight" style={{ color: c.color }}>{c.parent_name}</h3>
              <p className="text-[14px] text-slate-400">({c.technical_name})</p>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-700">{c.meaning}</p>
              <p className="mt-3 text-[14px] text-slate-500"><span className="font-semibold text-slate-700">What this looks like at home:</span> {c.home_line}</p>
            </div>
          ))}
        </div>

        {/* Full profile chart */}
        <div ref={chartRef} className="mt-12 rounded-3xl bg-white border border-violet-100 p-5 sm:p-7 shadow-lg">
          <h2 className="text-2xl font-extrabold text-slate-900">The full profile</h2>
          <p className="mt-1 text-[14px] text-slate-500">Strength is shown by bar length and the word beside it.</p>
          <div className="mt-4 rounded-2xl cta-gradient px-4 py-3 text-center text-white text-[14px] sm:text-[15px] font-bold flex items-center justify-center gap-2" data-testid="discover-expand-hint">
            <MousePointerClick className="h-5 w-5 shrink-0" /> Click on every strength below to learn more about it
          </div>
          <div className="mt-6"><Chart report={report} /></div>
        </div>

        {/* Scale explained */}
        <div className="mt-8 rounded-3xl bg-white border border-violet-100 p-5 sm:p-7">
          <h2 className="text-xl font-extrabold text-slate-900">The scale explained</h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {report.band_scale.map((b) => (
              <div key={b.label} className="flex items-center gap-3 rounded-xl border border-violet-50 p-2.5">
                <span className="h-4 w-8 shrink-0 rounded-full" style={{ backgroundColor: b.color }} />
                <span className="text-[14px]"><span className="font-bold text-slate-800">{b.label}</span> <span className="text-slate-500">, {b.meaning}</span></span>
              </div>
            ))}
          </div>
          <p className="mt-4 text-[14px] leading-relaxed text-slate-500">Every child shows all eight. A shorter bar does not mean weakness; it means that channel is not your child's current default route for learning.</p>
        </div>

        {/* Honest framing */}
        <p className="mt-8 rounded-2xl bg-violet-50 border border-violet-100 p-5 text-[14px] leading-relaxed text-violet-900">
          This is a strengths and tendency profile, not a test with a pass or fail, and not an IQ score. It reflects how your child prefers to approach new situations today.
        </p>

        {/* Career Deep Dive */}
        <div className="mt-10 rounded-[28px] border-2 border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50 p-6 sm:p-8" data-testid="discover-career-deepdive">
          <div className="flex items-center gap-3">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl cta-gradient text-white shadow-lg shadow-violet-300/40"><Compass className="h-6 w-6" /></span>
            <div>
              <p className="text-[12px] font-bold uppercase tracking-wide text-violet-500">Take it further</p>
              <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900">A Career Deep Dive for {child}</h2>
            </div>
          </div>
          <p className="mt-4 text-[15px] leading-relaxed text-slate-700">
            Want to go deeper into {child}'s future career direction, mapped to their <strong>natural &amp; gifted ability</strong>? A genuine career deep dive doesn't rely on this one learning-style layer alone — it needs {child}'s <strong>complete cognitive profile</strong>. Once the full picture is in, we can point precisely to the fields and roles where your child is naturally wired to thrive.
          </p>
          <div className="mt-4 flex items-start gap-2.5 rounded-2xl bg-white/70 border border-violet-100 p-3.5">
            <Lock className="h-5 w-5 shrink-0 text-violet-500 mt-0.5" />
            <p className="text-[14px] leading-relaxed text-slate-600">The Career Deep Dive unlocks only with the full MapMyKid assessment, which maps the complete cognitive profile behind your child's natural and gifted ability.</p>
          </div>
          <div className="mt-6">
            <Link to={ASSESSMENT_URL}><Button className="cta-gradient text-white rounded-full h-13 px-6 py-3.5 text-[16px] font-bold w-full sm:w-auto" data-testid="discover-deepdive-cta">Unlock the Career Deep Dive <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
          </div>
        </div>

        {/* Upsell */}
        <div className="mt-12 rounded-[28px] cta-gradient p-6 sm:p-10 text-white text-center relative overflow-hidden">
          <div className="absolute inset-0 grain opacity-60" />
          <div className="relative">
            <h2 className="text-2xl sm:text-3xl font-extrabold">This is one layer. Your child's full cognitive profile has many more.</h2>
            <p className="mt-3 text-[16px] text-white/85 max-w-2xl mx-auto">Learning style tells you which channel works. The MapMyKid full assessment maps the factors that actually drive academic outcomes.</p>
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {FACTORS.map((f) => (
                <div key={f.name} className="rounded-2xl bg-white/10 backdrop-blur border border-white/20 p-4 text-left">
                  <f.icon className="h-6 w-6 text-white" />
                  <p className="mt-2 text-[15px] font-bold">{f.name}</p>
                  <p className="text-[13px] text-white/80 leading-snug">{f.line}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 flex justify-center">
              <Link to={ASSESSMENT_URL}><Button className="bg-white text-violet-700 hover:bg-violet-50 rounded-full h-14 px-7 text-[16px] font-bold w-full sm:w-auto" data-testid="discover-book-cta">Explore the Full MapMyKid Assessment <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
            </div>
          </div>
        </div>
      </div>

      {/* sticky mobile CTA */}
      {showSticky && (
        <div className="fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-violet-100 p-3 sm:hidden">
          <Link to={ASSESSMENT_URL}><Button className="w-full cta-gradient text-white rounded-full h-12 text-[16px] font-bold">Explore the Full Assessment <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
        </div>
      )}
    </div>
  );
}
